import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export interface Module {
  id: number;
  name: string;
  url: string;
  enabled: boolean;
  isAdminDisabled?: boolean;
  permissions: {
    id: number;
    name: string;
    url: string;
    enabled: boolean;
    isAdminDisabled?: boolean;
  }[];
}

export interface User {
  id: string | null;
  samId: string | null;
  email: string;
  accountType: string;
  role: string;
  modules: Module[];
  welcomeModuleId: string | null;
  status: string;
  ppo?: string;
}

interface UsersResponse {
  content: User[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

interface UseUsersParams {
  page?: number;
  size?: number;
  designatedOperator?: string;
  email?: string;
  /**
   * Account type filter parameter using actual enum values like 'IPC_USER' or 'MAILBOX'
   * Values should come from the AccountTypes constant, not the display labels
   */
  accountType?: string;
}

const fetchUsers = async ({ page = 0, size = 10, designatedOperator, email, accountType }: UseUsersParams): Promise<UsersResponse> => {
  const params: Record<string, any> = { page, size };
  
  // Add designatedOperator to params if it exists and is not 'ALL'
  if (designatedOperator && designatedOperator !== 'ALL') {
    params.designatedOperator = designatedOperator;
  }
  
  // Add email parameter if it exists for email/SAM ID filtering
  if (email && email.trim() !== '') {
    params.email = email.trim();
  }
  
  // Add accountType parameter if it exists and is not 'all' - should be 'IPC_USER' or 'MAILBOX'
  if (accountType && accountType !== 'all') {
    params.accountType = accountType;
  }
  
  const response = await axios.get('/user/all', { params });
  return response.data;
};

export function useUsers(params: UseUsersParams = {}) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => fetchUsers(params),
  });
} 